import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { serviceJsonLd } from "@/lib/service-jsonld";
import { faqPageJsonLd, type FaqItem } from "@/lib/faq-jsonld";
import { FaqAccordion } from "@/components/site/FaqAccordion";

const netwerkFaqs: FaqItem[] = [
  {
    q: "Welke merken netwerkapparatuur gebruiken jullie?",
    a: "We werken met A-merken zoals Ubiquiti UniFi, MikroTik en TP-Link Omada. De keuze hangt af van het aantal gebruikers, gewenste snelheid en of centraal beheer nodig is.",
  },
  {
    q: "Kunnen jullie mijn WiFi-dekking in het hele huis of pand oplossen?",
    a: "Ja. We meten met professionele software de dekking, plaatsen access points op strategische punten en configureren één naadloos WiFi-netwerk zonder dode hoeken.",
  },
  {
    q: "Leggen jullie ook nieuwe UTP- of glasvezelbekabeling aan?",
    a: "Ja. Van één netwerkpunt tot een complete patchkast met tientallen aansluitingen — inclusief certificering van de kabels en nette afwerking in wandcontactdozen of plafondkasten.",
  },
  {
    q: "Kunnen jullie het netwerk op afstand beheren?",
    a: "Ja. Met UniFi- of Omada-controllers monitoren we het netwerk op afstand, doen firmware-updates en lossen we storingen vaak op zonder dat u op ons hoeft te wachten.",
  },
  {
    q: "Is netwerktechniek te combineren met camera’s en toegangscontrole?",
    a: "Zeker. Wij ontwerpen het netwerk direct met voldoende PoE-poorten, VLAN-scheiding en bandbreedte zodat camera’s, intercoms en toegangscontrole veilig samen draaien.",
  },
];
import {
  Network,
  Wifi,
  Cable,
  Router,
  Server,
  Globe,
  Settings,
  Shield,
  CheckCircle2,
  ArrowRight,
  PhoneCall,
  ClipboardList,
  CalendarCheck,
  HardHat,
  Wrench,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ContactCta } from "@/components/site/ContactCta";
import netwerkenCtaImg from "@/assets/netwerken-cta.png.asset.json";

export const Route = createFileRoute("/netwerktechniek")({
  head: () => ({
    meta: [
      { title: "Netwerktechniek — Elvion Group Nederland helpt u graag verder" },
      {
        name: "description",
        content:
          "Professionele netwerktechniek: WiFi, bekabeling, switches, routers, access points en glasvezel. Advies, installatie en onderhoud door Elvion Group.",
      },
      { property: "og:title", content: "Netwerktechniek — Elvion Group Nederland helpt u graag verder" },
      {
        property: "og:description",
        content:
          "Snelle, betrouwbare netwerken voor thuis en op kantoor. Van WiFi tot glasvezel: Elvion Group Nederland helpt u graag verder.",
      },
      { property: "og:url", content: "https://elvion-group.nl/netwerktechniek" },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/netwerktechniek" }],
    scripts: [
      breadcrumbJsonLd([{ name: "Netwerktechniek", path: "/netwerktechniek" }]),
      serviceJsonLd({
        path: "/netwerktechniek",
        name: "Netwerktechniek door Elvion Group Nederland",
        description:
          "Professionele netwerken voor thuis en op kantoor: WiFi, UTP- en glasvezelbekabeling, switches, routers, access points, serverkasten en beheer.",
        serviceType: "Netwerktechniek",
        offerCatalog: [
          "WiFi & access points",
          "Structurele bekabeling",
          "Switches & routers",
          "Serverkast & patchkast",
          "Glasvezel",
          "Beheer en monitoring",
        ],
      }),
      faqPageJsonLd(netwerkFaqs, "https://elvion-group.nl/netwerktechniek#faq"),
    ],
  }),
  component: NetwerktechniekPage,
});

const services = [
  {
    icon: Wifi,
    title: "WiFi & access points",
    body: "Dekkende, snelle WiFi voor woning, kantoor, winkel of terrein. We werken met professionele access points van Ubiquiti UniFi en soortgelijke A-merken, inclusief centraal beheer.",
  },
  {
    icon: Cable,
    title: "Structurale bekabeling",
    body: "Nette aanleg van UTP- en glasvezelbekabeling, patchkasten en wandcontactdozen. Voor een stabiel netwerk dat jaren meegaat.",
  },
  {
    icon: Router,
    title: "Switches & routers",
    body: "Keuze en configuratie van managed switches, routers, firewalls en PoE-oplossingen. Afgestemd op het aantal gebruikers en de gewenste performance.",
  },
  {
    icon: Server,
    title: "Serverkast & patchkast",
    body: "Opbouw en inrichting van serverkasten en patchkasten. Netjes, overzichtelijk en volgens de geldende normen voor koeling en voeding.",
  },
  {
    icon: Globe,
    title: "Glasvezel",
    body: "Glasvezelverbindingen voor hoge bandbreedte en lange afstanden. Van spleten en lassen tot het aansluiten van transceivers en patchpanelen.",
  },
  {
    icon: Settings,
    title: "Beheer en monitoring",
    body: "Centraal netwerkbeheer, VLAN-configuraties, gastennetwerken en monitoring. Zo houdt je controle over veiligheid, snelheid en beschikbaarheid.",
  },
];

const process = [
  {
    icon: PhoneCall,
    title: "1. Vrijblijvend contact",
    body: "We bespreken je wensen, het aantal gebruikers, het pand en de gewenste toepassingen. Ook plannen we indien nodig een schouw op locatie.",
  },
  {
    icon: ClipboardList,
    title: "2. Advies en offerte",
    body: "Je ontvangt een helder advies met een transparante offerte voor apparatuur, bekabeling en installatie.",
  },
  {
    icon: CalendarCheck,
    title: "3. Installatie",
    body: "Onze gecertificeerde monteurs leggen de bekabeling, plaatsen apparatuur en configureren het netwerk netjes en conform de geldende normen.",
  },
  {
    icon: HardHat,
    title: "4. Oplevering",
    body: "We leveren op met uitleg, documentatie en garantie. Daarna staan we voor je klaar bij vragen, uitbreidingen of storingen.",
  },
];

const certifications = [
  "Erkend installatiebedrijf",
  "Ubiquiti UniFi specialist",
  "Glasvezel- en UTP-expertise",
  "VCA-gecertificeerde monteurs",
  "Landelijk werkgebied",
  "Garantie op werk en materiaal",
];

function NetwerktechniekPage() {
  return (
    <>
      <Header variant="shop" />
      <main>
        <ContactCta
          imageUrl={netwerkenCtaImg.url}
          imageAlt="Elvion Group Nederland netwerktechniek"
          title="Netwerktechniek laten installeren?"
          description="Onze specialisten adviseren en installeren complete netwerkoplossingen: WiFi, bekabeling, switches, routers en glasvezel. Professioneel, betrouwbaar en schaalbaar."
        />

        {/* Hero */}
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-orange/30 bg-orange/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange">
                <Network className="h-3.5 w-3.5" />
                Netwerkspecialist
              </span>
              <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                Netwerktechniek voor woning en bedrijf
              </h1>
              <p className="mt-4 max-w-xl text-base text-text-secondary sm:text-lg">
                Van een betrouwbaar thuisnetwerk tot een professioneel
                bedrijfsnetwerk: Elvion Group Nederland levert, installeert en onderhoudt
                complete netwerkoplossingen. Altijd snel, stabiel en veilig.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/offerte-aanvragen"
                  className="inline-flex items-center gap-2 rounded-md bg-orange px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
                >
                  Vraag een offerte aan
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/klantenservice"
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
                >
                  Plan een schouw
                </Link>
              </div>
              <ul className="mt-6 grid gap-2 text-sm text-text-secondary sm:grid-cols-2">
                {certifications.slice(0, 4).map((c) => (
                  <li key={c} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-orange" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-orange/20 via-orange/5 to-transparent blur-2xl" />
              <div className="relative rounded-2xl border border-border bg-card p-8 shadow-lg">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Wifi, label: "WiFi" },
                    { icon: Cable, label: "UTP" },
                    { icon: Router, label: "Router" },
                    { icon: Server, label: "Server" },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex flex-col items-center gap-3 rounded-xl border border-border bg-background p-5 text-center"
                    >
                      <div className="rounded-full bg-orange/10 p-3 text-orange">
                        <item.icon className="h-6 w-6" />
                      </div>
                      <span className="text-sm font-semibold">{item.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 rounded-xl bg-background p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-orange">
                    Direct advies?
                  </p>
                  <p className="mt-1 text-sm text-text-secondary">
                    Bel ons voor een vrijblijvend gesprek of een snelle prijsindicatie.
                  </p>
                  <a
                    href="tel:+31000000000"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-orange"
                  >
                    <PhoneCall className="h-4 w-4" />
                    Neem contact op
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services grid */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Onze netwerkoplossingen
              </h2>
              <p className="mt-3 text-base text-text-secondary">
                Wij combineren betrouwbare A-merken met vakkundige installatie.
                Zo krijg je een netwerk dat overal goed werkt.
              </p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {services.map((s) => (
                <div
                  key={s.title}
                  className="group rounded-xl border border-border bg-background p-6 transition-all hover:border-orange hover:shadow-md"
                >
                  <div className="inline-flex rounded-lg bg-orange/10 p-3 text-orange transition-colors group-hover:bg-orange group-hover:text-white">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold">{s.title}</h3>
                  <p className="mt-2 text-sm text-text-secondary">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Zo werken wij
            </h2>
            <p className="mt-3 text-base text-text-secondary">
              Van eerste contact tot oplevering: een helder traject zonder verrassingen.
            </p>
          </div>

          <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {process.map((step) => (
              <li
                key={step.title}
                className="relative rounded-xl border border-border bg-card p-6"
              >
                <div className="inline-flex rounded-lg bg-orange/10 p-3 text-orange">
                  <step.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-base font-bold">{step.title}</h3>
                <p className="mt-2 text-sm text-text-secondary">{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Detailed sections */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl space-y-12 px-4 py-14 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  WiFi en access points
                </h2>
                <p className="mt-3 text-text-secondary">
                  Geen dode hoeken meer en geen haperende videocalls. Wij ontwerpen
                  een WiFi-netwerk dat dekkend is én snel blijft, ook met veel
                  apparaten tegelijk. We werken onder andere met Ubiquiti UniFi:
                  professionele access points met centraal beheer, gastennetwerken
                  en naadloze roaming.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Mesh- en multi-AP-oplossingen</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> VLAN's en gastennetwerken</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Centraal dashboard voor beheer</li>
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  Bekabeling, switches en routers
                </h2>
                <p className="mt-3 text-text-secondary">
                  Een goed netwerk begint met goede bekabeling. Wij leggen UTP- en
                  glasvezelbekabeling netjes weg, plaatsen patchkasten en
                  configureren managed switches, routers en firewalls. Zo creëren
                  we een stabiele basis voor internet, telefonie, camera's en
                  andere systemen.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-text-secondary">
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> UTP Cat6 / Cat6A / Cat7 en glasvezel</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Managed switches en PoE</li>
                  <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" /> Firewall en netwerkbeveiliging</li>
                </ul>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-background p-8 lg:p-10">
              <div className="flex items-start gap-4">
                <div className="hidden rounded-lg bg-orange/10 p-3 text-orange sm:inline-flex">
                  <Shield className="h-7 w-7" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                    Waarom kiezen voor Elvion Group Nederland?
                  </h2>
                  <p className="mt-3 text-text-secondary">
                    We denken mee, werken met kwalitatieve A-merken en leveren
                    altijd netjes op. Of het nu gaat om een beter thuisnetwerk of
                    een complete netwerk infrastructuur voor een bedrijfspand: wij
                    zorgen voor een oplossing die past bij jouw situatie.
                  </p>
                  <div className="mt-6 grid gap-4 sm:grid-cols-3">
                    <div className="rounded-lg border border-border p-4">
                      <Wifi className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold">A-merken</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Ubiquiti UniFi en andere betrouwbare merken.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border p-4">
                      <Cable className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold">Nette aanleg</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Zorgvuldige bekabeling en overzichtelijke patchkasten.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border p-4">
                      <Wrench className="h-5 w-5 text-orange" />
                      <p className="mt-2 text-sm font-semibold">Service</p>
                      <p className="mt-1 text-xs text-text-secondary">
                        Onderhoud, updates en storingsdienst beschikbaar.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why us */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Waarom Elvion Group Nederland u helpt
          </h2>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {certifications.map((c) => (
              <div
                key={c}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3"
              >
                <CheckCircle2 className="h-5 w-5 shrink-0 text-orange" />
                <span className="text-sm font-medium">{c}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-7xl px-4 py-14 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Jouw netwerk in goede handen?
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-text-secondary">
              Vraag vrijblijvend een offerte aan of plan een schouw op locatie.
              We denken mee over de beste netwerkoplossing voor jouw situatie.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/offerte-aanvragen"
                className="inline-flex items-center gap-2 rounded-md bg-orange px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
              >
                Offerte opvragen
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/klantenservice"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
              >
                Contact opnemen
              </Link>
            </div>
          </div>
        </section>
        <section className="border-t border-border bg-background py-16">
          <div className="container mx-auto px-4">
            <FaqAccordion
              items={netwerkFaqs}
              title="Veelgestelde vragen — netwerktechniek"
              intro="Antwoorden op de meestgestelde vragen over WiFi, bekabeling en netwerkbeheer."
              id="faq-netwerk"
            />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
